import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  Alert,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import { Block as BlockIcon, Add as AddIcon } from '@mui/icons-material';
import api from '../services/api';
import { 
  TEACHERS_STUDENTS,
  TEACHER_MANAGED_STUDENTS,
  SUBJECTS, 
  ACADEMIC_YEARS 
} from '../config/Config';
import { filterPublished } from '../utils/publishedFilter';

const AddStudentDialog = ({ open, onClose, editingStudent = null, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [resendPayload, setResendPayload] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    display_name: '',
    academic_year_id: '',
    subject: '',
    notes: '',
    parent_email: ''
  });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteParentEmail, setInviteParentEmail] = useState('');
  /** New student only: `invite_platform` = email invite; `self_managed` = roster placeholder, no invite. */
  const [addMode, setAddMode] = useState('invite_platform');

  // Fetch subjects and academic years
  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const [subRes, yrRes] = await Promise.all([
            api.get(SUBJECTS),
            api.get(ACADEMIC_YEARS + '?published_only=true')
          ]);
          const subs = Array.isArray(subRes.data) ? subRes.data : (subRes.data.results || []);
          const yrs = Array.isArray(yrRes.data) ? yrRes.data : (yrRes.data.results || []);
          setSubjects(subs);
          setAcademicYears(filterPublished(yrs));
        } catch (e) {
          setError('Failed to load form data');
        }
      };
      fetchData();
    }
  }, [open]);

  // Reset form when editing student changes
  useEffect(() => {
    if (editingStudent) {
      setFormData({
        email: editingStudent.email || '',
        display_name: editingStudent.display_name || '',
        academic_year_id: editingStudent.academic_year || editingStudent.academic_year_id || '',
        subject: editingStudent.subject || editingStudent.subject_id || '',
        notes: editingStudent.notes || '',
        parent_email: editingStudent.parent_email || ''
      });
      setInviteEmail((editingStudent.email || '').trim());
      setInviteParentEmail('');
    } else {
      setFormData({
        email: '',
        display_name: '',
        academic_year_id: '',
        subject: '',
        notes: '',
        parent_email: ''
      });
      setInviteEmail('');
      setInviteParentEmail('');
    }
  }, [editingStudent]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open && !editingStudent) {
      // Reset form when dialog opens for new student
      setFormData({
        email: '',
        display_name: '',
        academic_year_id: '',
        subject: '',
        notes: '',
        parent_email: ''
      });
      setInviteEmail('');
      setInviteParentEmail('');
      setAddMode('invite_platform');
      // Clear any previous errors and success messages
      setError('');
      setSuccessMessage('');
    }
  }, [open, editingStudent]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
  
    try {
      let res;
  
      if (editingStudent) {
        if (editingStudent.source_type === 'managed_student') {
          const dn = (formData.display_name || '').trim();
          if (!dn) {
            setError('Display name is required.');
            setLoading(false);
            return;
          }
          const updatePayload = {
            display_name: dn,
            notes: formData.notes || '',
            academic_year: formData.academic_year_id || null,
            subject: formData.subject || null,
          };
          res = await api.patch(
            `${TEACHER_MANAGED_STUDENTS}${editingStudent.managed_student_id}/`,
            updatePayload
          );
          if (res.data?.success === false) {
            setError(res.data.message || 'Update failed');
            return;
          }
          if (onSuccess) onSuccess();
          onClose();
          return;
        }

        // Update existing platform relationship
        // Note: student_email is read-only, so we don't send it
        // The serializer expects 'subject' and 'academic_year', not 'subject_id' and 'academic_year_id'
        const updatePayload = {
          academic_year: formData.academic_year_id || null,
          subject: formData.subject || null,
          notes: formData.notes || ''
        };
        
        res = await api.put(`${TEACHERS_STUDENTS}${editingStudent.relationship_id}/`, updatePayload);
        if (editingStudent.managed_student_id) {
          await api.patch(`${TEACHER_MANAGED_STUDENTS}${editingStudent.managed_student_id}/`, {
            display_name: (
              formData.display_name ||
              `${editingStudent.first_name || ''} ${editingStudent.last_name || ''}`.trim() ||
              editingStudent.email ||
              'Student'
            ),
            notes: formData.notes || '',
            academic_year: formData.academic_year_id || null,
            subject: formData.subject || null,
          });
        }
  
        if (res.data?.success === false) {
          setError(res.data.message || 'Update failed');
          return;
        }

        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
        
        // Close dialog immediately after update
        onClose();
        return; // Exit early to avoid the setTimeout below
      } else {
        if (addMode === 'self_managed') {
          const dn = (formData.display_name || '').trim();
          if (!dn) {
            setError('Display name is required for a self-managed roster entry.');
            setLoading(false);
            return;
          }
          if (!formData.subject || !formData.academic_year_id) {
            setError('Subject and grade are required.');
            setLoading(false);
            return;
          }
          const emailLower = (formData.email || '').trim().toLowerCase();
          res = await api.post(TEACHER_MANAGED_STUDENTS, {
            display_name: dn,
            email: emailLower || undefined,
            notes: formData.notes || '',
            academic_year: formData.academic_year_id || null,
            subject: formData.subject || null,
          });
          setError('');
          setSuccessMessage('Student added to your roster. You can invite them to the platform later from their row.');
          if (onSuccess) {
            onSuccess();
          }
          setTimeout(() => {
            onClose();
          }, 1500);
          return;
        }

        // Parent email must be different from student email
        const studentEmail = (formData.email || '').trim().toLowerCase();
        const parentEmailVal = (formData.parent_email || '').trim().toLowerCase();
        if (parentEmailVal && parentEmailVal === studentEmail) {
          setError('Parent email must be different from the student email.');
          setLoading(false);
          return;
        }
        // New invitation flow – backend enforces duplicate/pending logic.
        res = await api.post(TEACHERS_STUDENTS, {
          student_email: formData.email,
          academic_year_id: formData.academic_year_id,
          subject_id: formData.subject,
          notes: formData.notes || '',
          is_active: false,
          parent_email: formData.parent_email || undefined
        });
  
        if (res.data?.success === false) {
          // Special handling when invite already exists but is still pending.
          if (res.data.action === 'already_invited') {
            // Show inline warning and open a Yes/No dialog.
            setError(res.data.message || 'An invitation has already been sent to this student and is waiting for them to register.');
            setResendPayload({
              email: formData.email,
              academic_year_id: formData.academic_year_id,
              subject_id: formData.subject,
              parent_email: formData.parent_email,
            });
            setResendConfirmOpen(true);
            setLoading(false);
            return;
          } else {
            setError(res.data.message || 'Invitation failed');
            return;
          }
        } else {
          // Set success message in dialog instead of snackbar
          setError(''); // Clear any previous errors
          setSuccessMessage(res.data?.message || 'Invitation sent successfully');
        }
      }

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
      
      // Close dialog after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
  
    } catch (error) {
      // Handle rate limiting specifically
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        let retryMessage = 'Too many requests. Please try again later.';
        
        if (retryAfter) {
          const retrySeconds = parseInt(retryAfter);
          if (retrySeconds < 60) {
            retryMessage = `Too many requests. You can try after ${retrySeconds} seconds.`;
          } else if (retrySeconds < 3600) {
            const minutes = Math.ceil(retrySeconds / 60);
            retryMessage = `Too many requests. You can try after ${minutes} minutes.`;
          } else {
            const hours = Math.ceil(retrySeconds / 3600);
            retryMessage = `Too many requests. You can try after ${hours} hours.`;
          }
        }
        
        setError(retryMessage);
        return;
      }
      
      const raw = error?.response?.data;
      let detail = typeof raw === 'string' ? raw : raw?.detail || JSON.stringify(raw);
    
      if (typeof detail === 'object') {
        detail = detail.message || String(detail);
      }
    
      setError(detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendYes = async () => {
    if (!resendPayload) {
      setResendConfirmOpen(false);
      return;
    }
    try {
      setLoading(true);
      const resendRes = await api.post(TEACHERS_STUDENTS, {
        student_email: resendPayload.email,
        academic_year_id: resendPayload.academic_year_id,
        subject_id: resendPayload.subject_id,
        is_active: false,
        force_resend: true,
        parent_email: resendPayload.parent_email || undefined,
      });
      if (resendRes.data?.success === false) {
        setError(resendRes.data.message || 'Re-invitation failed');
        return;
      }
      setError('');
      setSuccessMessage(resendRes.data?.message || 'Re-invitation sent successfully');
      if (onSuccess) {
        onSuccess();
      }
      setResendConfirmOpen(false);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError('Re-invitation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendNo = () => {
    setResendConfirmOpen(false);
    onClose();
  };

  const handleManagedInvite = async () => {
    if (!editingStudent || editingStudent.source_type !== 'managed_student') return;
    const studentEmail = (inviteEmail || '').trim().toLowerCase();
    const parentVal = (inviteParentEmail || '').trim().toLowerCase();
    if (!studentEmail) {
      setError('Student email is required to send an invitation.');
      return;
    }
    if (parentVal && parentVal === studentEmail) {
      setError('Parent email must be different from the student email.');
      return;
    }
    try {
      setLoading(true);
      const response = await api.post(
        `${TEACHER_MANAGED_STUDENTS}${editingStudent.managed_student_id}/invite/`,
        {
          student_email: studentEmail,
          academic_year_id: formData.academic_year_id || undefined,
          subject_id: formData.subject || undefined,
          parent_email: parentVal || undefined,
        }
      );
      if (response.data?.success === false) {
        setError(response.data.message || 'Invitation failed');
        return;
      }
      setError('');
      setSuccessMessage(response.data?.message || 'Invitation sent.');
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many invitation requests. Please try again later.');
        return;
      }
      const raw = err?.response?.data;
      const detail = typeof raw === 'string' ? raw : raw?.message || raw?.detail || 'Invitation failed';
      setError(typeof detail === 'string' ? detail : 'Invitation failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle remove student (deactivate platform relationship, or archive managed roster row)
  const handleRemoveStudent = async (relationshipId, opts = {}) => {
    const { managedId } = opts;
    const confirmMsg = managedId
      ? 'Remove this roster entry? You can add them again later.'
      : 'Are you sure you want to remove this student? This action cannot be undone and the relationship will need to be reinitiated.';
    if (window.confirm(confirmMsg)) {
      try {
        setLoading(true);
        if (managedId) {
          await api.delete(`${TEACHER_MANAGED_STUDENTS}${managedId}/`);
        } else {
          await api.put(`${TEACHERS_STUDENTS}${relationshipId}/`, {
            is_active: false
          });
        }
        
        setError('');
        setSuccessMessage(
          managedId ? 'Roster entry removed.' : 'Student removed successfully. The relationship has been deactivated.'
        );
        
        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
        
        // Close dialog after a brief delay to show success message
        setTimeout(() => {
          onClose();
        }, 3000);
      } catch (err) {
        // Handle rate limiting specifically
        if (err.response?.status === 429) {
          const retryAfter = err.response.headers['retry-after'];
          let retryMessage = 'Too many requests. Please try again later.';
          
          if (retryAfter) {
            const retrySeconds = parseInt(retryAfter);
            if (retrySeconds < 60) {
              retryMessage = `Too many requests. You can try after ${retrySeconds} seconds.`;
            } else if (retrySeconds < 3600) {
              const minutes = Math.ceil(retrySeconds / 60);
              retryMessage = `Too many requests. You can try after ${minutes} minutes.`;
            } else {
              const hours = Math.ceil(retrySeconds / 3600);
              retryMessage = `Too many requests. You can try after ${hours} hours.`;
            }
          }
          
          setError(retryMessage);
          return;
        }
        
        setError('Failed to remove student');
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle re-invite student (reactivate relationship)
  const handleReinviteStudent = async (student) => {
    try {
      setLoading(true);
      // Use the reinvite endpoint
      const response = await api.post(`${TEACHERS_STUDENTS}${student.relationship_id}/reinvite/`);
      
      if (response.data?.success === false) {
        setError(response.data.message || 'Re-invitation failed');
        return;
      }
      
      // Set success message in dialog instead of snackbar
      setError(''); // Clear any previous errors
      setSuccessMessage(`Re-invitation sent successfully to ${student.email}`);
      
      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
      
      // Close dialog after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      // Handle rate limiting specifically
      if (err.response?.status === 429) {
        const retryAfter = err.response.headers['retry-after'];
        let retryMessage = 'Too many requests. Please try again later.';
        
        if (retryAfter) {
          const retrySeconds = parseInt(retryAfter);
          if (retrySeconds < 60) {
            retryMessage = `Too many requests. You can try after ${retrySeconds} seconds.`;
          } else if (retrySeconds < 3600) {
            const minutes = Math.ceil(retrySeconds / 60);
            retryMessage = `Too many requests. You can try after ${minutes} minutes.`;
          } else {
            const hours = Math.ceil(retrySeconds / 3600);
            retryMessage = `Too many requests. You can try after ${hours} hours.`;
          }
        }
        
        setError(retryMessage);
        return;
      }
      
      setError('Failed to re-invite student');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccessMessage('');
    onClose();
  };

  const selectedAcademicYear = academicYears.find(
    (year) => String(year.id) === String(formData.academic_year_id)
  );
  const gradeAllowsParentEmail = Number(selectedAcademicYear?.year) <= 10;
  const showParentEmailField =
    !editingStudent && addMode === 'invite_platform' && gradeAllowsParentEmail;
  const shouldOfferParentEmailManagedInvite =
    editingStudent?.source_type === 'managed_student' && gradeAllowsParentEmail;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {editingStudent 
            ? (() => {
                if (editingStudent.source_type === 'managed_student') {
                  return `Edit roster: ${editingStudent.display_name || 'Student'}`;
                }
                const firstName = (editingStudent.first_name || '').trim();
                const lastName = (editingStudent.last_name || '').trim();
                // Guard against records where last_name duplicates first_name (e.g. "Shouvik banerjee Shouvik banerjee")
                const fullName = firstName.toLowerCase() === lastName.toLowerCase()
                  ? firstName
                  : `${firstName} ${lastName}`.trim();
                const displayName = fullName || editingStudent.email || 'Student';
                return `Edit Student: ${displayName}`;
              })()
            : 'Add New Student'}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}
          
          {editingStudent?.source_type === 'managed_student' && (
            <TextField
              autoFocus
              margin="dense"
              name="display_name"
              label="Display name"
              fullWidth
              variant="outlined"
              value={formData.display_name}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
          )}

          {!editingStudent && (
            <FormControl component="fieldset" sx={{ mb: 2 }} variant="standard">
              <Typography component="legend" variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                How do you want to add this student?
              </Typography>
              <RadioGroup
                value={addMode}
                onChange={(e) => {
                  setAddMode(e.target.value);
                  setError('');
                }}
              >
                <FormControlLabel
                  value="invite_platform"
                  control={<Radio />}
                  label="Invite to join the platform — they receive an email to register and join your group."
                />
                <FormControlLabel
                  value="self_managed"
                  control={<Radio />}
                  label="Self-managed roster only — no invitation yet. Track tests and materials until they join."
                />
              </RadioGroup>
            </FormControl>
          )}

          {!editingStudent && addMode === 'self_managed' && (
            <TextField
              autoFocus
              margin="dense"
              name="display_name"
              label="Display name"
              fullWidth
              variant="outlined"
              value={formData.display_name}
              onChange={handleChange}
              required
              helperText="How this student appears on your roster (e.g. full name)."
              sx={{ mb: 2 }}
            />
          )}

          {!editingStudent && (
            <TextField
              autoFocus={addMode === 'invite_platform'}
              margin="dense"
              name="email"
              label={addMode === 'self_managed' ? 'Student email (optional)' : 'Student Email'}
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={handleChange}
              required={addMode === 'invite_platform'}
              helperText={
                addMode === 'self_managed'
                  ? 'Optional. You can send a platform invitation later from the student row.'
                  : undefined
              }
              sx={{ mb: 2 }}
            />
          )}
          {showParentEmailField && (
            <TextField
              margin="dense"
              name="parent_email"
              label="Parent email ID (optional)"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.parent_email}
              onChange={handleChange}
              helperText="Optional. Use this only if you want to prefill the parent or guardian contact for grade 10 or below."
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            select
            margin="dense"
            name="subject"
            label="Subject"
            fullWidth
            variant="outlined"
            value={formData.subject}
            onChange={handleChange}
            required={editingStudent?.source_type !== 'managed_student'}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">
              <em>Select subject</em>
            </MenuItem>
            {subjects.map((s) => (
              <MenuItem value={s.id} key={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            margin="dense"
            name="academic_year_id"
            label="Grade / Academic Year"
            fullWidth
            variant="outlined"
            value={formData.academic_year_id}
            onChange={handleChange}
            required={editingStudent?.source_type !== 'managed_student'}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">
              <em>Select year</em>
            </MenuItem>
            {academicYears.map((y) => (
              <MenuItem value={y.id} key={y.id}>
                {y.year}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            margin="dense"
            name="notes"
            label="Notes"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={formData.notes}
            onChange={handleChange}
          />
          
          {editingStudent?.source_type === 'managed_student' &&
            editingStudent.managed_status !== 'onboarded_linked' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ mb: 1, fontWeight: 600 }}>Platform invitation</Box>
              <TextField
                margin="dense"
                label="Student email (for invitation)"
                type="email"
                fullWidth
                variant="outlined"
                value={inviteEmail}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  if (error) setError('');
                }}
                sx={{ mb: 2 }}
              />
              {shouldOfferParentEmailManagedInvite && (
                <TextField
                  margin="dense"
                  label="Parent email (optional)"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={inviteParentEmail}
                  onChange={(e) => {
                    setInviteParentEmail(e.target.value);
                    if (error) setError('');
                  }}
                  helperText="Optional for grade 10 and below."
                  sx={{ mb: 2 }}
                />
              )}
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={handleManagedInvite}
                disabled={loading}
                startIcon={<AddIcon />}
              >
                {editingStudent.managed_status === 'invited_pending'
                  ? 'Resend platform invitation'
                  : 'Send platform invitation'}
              </Button>
            </Box>
          )}

          {editingStudent && editingStudent.source_type !== 'managed_student' && editingStudent.is_active && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Box sx={{ mb: 1 }}>
                ⚠️ Warning: This action cannot be undone. The relationship will need to be reinitiated.
              </Box>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => handleRemoveStudent(editingStudent.relationship_id)}
                disabled={loading}
                startIcon={<BlockIcon />}
              >
                Deactivate Student
              </Button>
            </Box>
          )}

          {editingStudent?.source_type === 'managed_student' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Box sx={{ mb: 1 }}>
                Remove this roster entry if you no longer need it. This does not delete a student account that has already joined.
              </Box>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => handleRemoveStudent(null, { managedId: editingStudent.managed_student_id })}
                disabled={loading}
                startIcon={<BlockIcon />}
              >
                Remove roster entry
              </Button>
            </Box>
          )}
          
          {editingStudent && editingStudent.source_type !== 'managed_student' && !editingStudent.is_active && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Box sx={{ mb: 1 }}>
                ℹ️ This student relationship has been deactivated. You can re-invite them to reactivate the relationship.
              </Box>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={() => handleReinviteStudent(editingStudent)}
                disabled={loading}
                startIcon={<AddIcon />}
              >
                Invite Again
              </Button>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleClose}
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {editingStudent ? 'Update' : 'Add'} Student
          </Button>
        </DialogActions>
      </form>
      </Dialog>

      {/* Confirm resend dialog (Yes/No) */}
      <Dialog open={resendConfirmOpen} onClose={handleResendNo} maxWidth="xs" fullWidth>
        <DialogTitle>Invitation already sent</DialogTitle>
        <DialogContent>
          An invitation has already been sent to this student and is waiting for them to register.
          <br />
          <br />
          Do you want to resend the invitation email?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResendNo} disabled={loading}>
            No
          </Button>
          <Button onClick={handleResendYes} color="primary" variant="contained" disabled={loading}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddStudentDialog;
